import { CollectionService, IJsonapiCollection, IJsonapiModel } from '@datx/jsonapi-angular';
import { Observable, of, switchMap } from 'rxjs';
import { IRequestOptions } from '@datx/jsonapi';
import { inject } from '@angular/core';
import { SpinnerService } from '../spinner/service/spinner.service';
import { Store } from '@ngxs/store';
import { FetchOptionsActions } from '../state/fetchOptions.actions';

export interface CollectionMetaData {
  page: {
    currentPage: number
    from: number
    lastPage: number
    perPage: number
    to: number
    total: number
  }
}

export interface IFetchOptions {
  pageNumber?: number
  pageSize?: number
  filters?: CollectionFilter[] | null
  filterFieldName?: string | null
  filter?: string | null
  sortFieldName?: string | null
  sortDirection?: string | null
  fields?: string[] | null
  includes?: string[]
  skipStoreDispatch?: boolean
}

/**
maybe use later
interface CollectionLinks {
  first: string;
  last: string;
  prev: string;
  next: string;
} */

export interface CollectionFilter {
  fieldName: string
  value: string | boolean
}

export abstract class BaseCollectionService<TModel extends IJsonapiModel, TCollection extends IJsonapiCollection> extends CollectionService<TModel, TCollection> {
  private _defaultPageSize = 25;
  spinnerService = inject(SpinnerService);
  private readonly store = inject(Store);

  public getAllWithPagination(requestOptions?: IRequestOptions, options?: IFetchOptions): Observable<[TModel[], CollectionMetaData]> {
    this.spinnerService.show();
    const pageNumber = options?.pageNumber ?? 1;
    const pageSize = options?.pageSize ?? this._defaultPageSize;

    const concatArray: string[] = [];
    if (pageSize > -1) {
      concatArray.push(`page[size]=${pageSize}`);
      concatArray.push(`page[number]=${pageNumber}`);
    }

    // Filter
    if (options?.filters && options.filters.length > 0) {
      options.filters.forEach(filter => {
        if (filter.fieldName.indexOf('.') > -1) {
          const filters = filter.fieldName.split('.');
          let filterFieldNames = '';

          filters.forEach((entry) => {
            filterFieldNames += `[${entry}]`;
          });

          concatArray.push(`filter${filterFieldNames}=${filter.value}`);
        }
        else {
          concatArray.push(`filter[${filter.fieldName}]=${filter.value}`);
        }
      });
    }
    else if (options?.filterFieldName && options?.filter) {
      concatArray.push(`filter[${options.filterFieldName}]=${options.filter}`);
    }

    // Sort order
    if (options?.sortFieldName && options?.sortDirection) {
      if (options.sortDirection === 'desc') {
        concatArray.push(`sort=-${options.sortFieldName}`);
      }
      else {
        concatArray.push(`sort=${options.sortFieldName}`);
      }
    }

    // Includes
    if (options?.includes) {
      let includes = '';
      options.includes.forEach((include, idx) => {
        includes += (idx > 0 ? ',' : '') + include;
      });
      concatArray.push(`include=${includes}`);
    }

    // Fields which should be contained in the result
    if (options?.fields && options.fields.length > 0) {
      concatArray.push(`fields[${this.ctor.type}]=${options.fields.join(',').replace(/\s/g, '')}`);
    }

    return this.getMany({
      ...requestOptions,
      queryParams: {
        ...requestOptions?.queryParams,
        custom: (requestOptions?.queryParams?.custom || []).concat(concatArray),
      },
    }).pipe(switchMap(response => {
      const r: [TModel[], CollectionMetaData] = [response.data as TModel[], response.meta as CollectionMetaData];
      const skipStoreDispatch = options?.skipStoreDispatch === true;
      if (!skipStoreDispatch) {
        this.store.dispatch(new FetchOptionsActions.SetFromResult(r[1]));
      }
      return of(r);
    }));
  }

  public getOneWithId(id: string, requestOptions?: IRequestOptions): Observable<TModel> {
    return this
      .getOne(
        id, {
          ...requestOptions,
        })
      .pipe(switchMap(response => {
        const r: TModel = response.data as TModel;
        return of(r);
      }));
  }
}
